import type { ReactiveElement } from 'lit';
import SlInput from '@shoelace-style/shoelace/dist/components/input/input.component.js';
import SlTextarea from '@shoelace-style/shoelace/dist/components/textarea/textarea.component.js';

/**
 * Browser autofill is off on every data-entry field in the app, set in ONE place.
 *
 * Why: feedback round 4 (issue #380). A tester clicked the browser's autofill suggestion on the
 * AS 2047 Test Report Number field and the browser filled the rest of the form from an earlier
 * report. This is a data-entry system: a value the browser remembers from some other record is
 * never the right value for this one, and autofill can only turn a typed record into a wrong one.
 * The rule and its scenarios live in documentation/ui-qa/forms/ (data entry autofill).
 *
 * The mechanism: Lit's `addInitializer` on Shoelace's own SlInput and SlTextarea classes. An
 * initializer runs inside every instance's constructor, so each `<sl-input>` and `<sl-textarea>`
 * in the app is born with `autocomplete = 'off'`, whichever module registered the tag and
 * whenever it did. All it needs is to be added before the first field is CONSTRUCTED, which
 * main.ts guarantees by importing this module before the app shell renders anything.
 *
 * Why not a registered subclass (the first design, and why this comment says so): Shoelace's
 * `define` returns silently when a same-version class already holds the tag, so registering a
 * subclass first looked like it would make every later wrapper import a no-op. It does in an
 * unbundled run, and it did in every unit test. In the served build it is the other way round:
 * ui/vite.config.ts `manualChunks` puts all of `@shoelace-style` into one `vendor` chunk, the
 * entry's first import is that chunk, and so every Shoelace wrapper's `define('sl-input')` runs
 * before any of main.ts's own modules. The subclass was the silent no-op, and the journey was
 * red. Nothing here rests on import order relative to registration.
 *
 * An explicit attribute still wins. The initializer runs during construction, and a custom
 * element's attributes are applied after construction, so `autocomplete="username"` on an element
 * overrides `off`. That is what keeps the sign-in page's `username`, `current-password`,
 * `new-password`, `one-time-code` and `email` hints working for password managers: it is the one
 * exemption, and it needs no code here.
 *
 * Coupling note: rests on Lit's `ReactiveElement.addInitializer` running in the constructor, on
 * neither Shoelace constructor assigning `autocomplete` itself (true in 2.20.1), and on SlTextarea
 * 2.20.1 declaring `autocomplete` without rendering it (see the textarea controller). Anything
 * that constructs a Shoelace field before this module runs would be born without the default.
 */

/** Every sl-input starts with autofill off; SlInput renders the property onto its inner input. */
SlInput.addInitializer((element: ReactiveElement) => {
  if (element instanceof SlInput) element.autocomplete = 'off';
});

/**
 * Every sl-textarea starts with autofill off, and the property reaches the inner control.
 *
 * Unlike SlInput, SlTextarea 2.20.1 declares an `autocomplete` property and never renders it: the
 * inner `<textarea>` template (chunk.Q3IETUAU.js) carries autocapitalize and autocorrect but no
 * autocomplete. So a default on the property alone would reach nothing. The controller copies the
 * property onto the inner control after every update, which is what Shoelace's own template would
 * have done, so an explicit hint set later reaches it too.
 */
SlTextarea.addInitializer((element: ReactiveElement) => {
  if (!(element instanceof SlTextarea)) return;
  const textarea = element;
  textarea.autocomplete = 'off';
  textarea.addController({
    hostUpdated() {
      const control = textarea.input;
      if (!control) return;
      // An empty value means the element opted out of any hint, which is the attribute's absence
      // rather than an empty attribute the browser would read as an invalid token.
      if (textarea.autocomplete) {
        if (control.getAttribute('autocomplete') !== textarea.autocomplete) {
          control.setAttribute('autocomplete', textarea.autocomplete);
        }
      } else {
        control.removeAttribute('autocomplete');
      }
    },
  });
});
